import React, { useEffect, useState } from 'react';
import Navbar from './Features/Navbar';
import hr from '../images/icons/hr.png';
import { Box, CircularProgress, Container } from '@mui/material';
import Footer from './Home/Footer';
import { Fade } from 'react-awesome-reveal';
import StrapiRichText from './StrapiRichText';
import { fetchStrapiSingleType } from '../services/strapiClient';
import { pickCmsText, pickCmsTitle } from '../utils/cmsContent';

const servicePageBodySx = {
  fontSize: '20px',
  lineHeight: 1.8,
  fontFamily: 'Montserrat',
  color: '#2f3036',
  fontWeight: 500,
  '& p': { marginTop: 0, marginBottom: '1rem' },
  '& p:last-child': { marginBottom: 0 },
  '& ul, & ol': { marginBottom: '1rem', paddingLeft: '1.25rem' },
  '& ul ul': { marginTop: '0.5rem', marginBottom: '0.5rem' },
  '& li': { marginBottom: '0.35rem' },
  '& strong': { fontWeight: 700 },
  '& a': { color: '#1398aa' },
  '& u': { textDecoration: 'underline', fontStyle: 'italic' },
  '& h2': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '34px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& h3': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '28px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& table': { borderCollapse: 'collapse', width: '100%', marginBottom: '2rem' },
  /* Bloc type « Attention » (Mahal, etc.) : dans Strapi, entourer le HTML avec class="service-callout" */
  '& .service-callout': {
    borderStyle: 'solid',
    borderRadius: '20px',
    backgroundColor: '#EBEAFF',
    borderColor: '#EBEAFF',
    marginBottom: '3rem',
    padding: '20px',
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '20px',
    color: '#2f3036',
    fontWeight: 600,
  },
  '& .service-callout p': { marginTop: 0, marginBottom: '1rem' },
  '& .service-callout p:last-child': { marginBottom: 0 },
};

/**
 * Page Services : single type Strapi (comme les pages À propos) + composant statique si body vide.
 */
export default function ServicePageShell({
  strapiApiId,
  defaultSectionLabel = 'Services',
  defaultTitle,
  StaticFallback,
  fadeTriggerOnce = true,
}) {
  const [sectionLabel, setSectionLabel] = useState(defaultSectionLabel);
  const [title, setTitle] = useState(defaultTitle);
  const [body, setBody] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${title} | Tsahal Connection`;
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const remote = await fetchStrapiSingleType(strapiApiId);
        if (cancelled) return;
        if (remote) {
          setSectionLabel(pickCmsText(remote.sectionLabel, defaultSectionLabel));
          setTitle(pickCmsTitle(remote.title, defaultTitle));
          const raw = remote.body;
          const trimmed = raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
          setBody(trimmed);
        } else {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setBody(null);
        }
      } catch {
        if (!cancelled) {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setBody(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [strapiApiId, defaultSectionLabel, defaultTitle]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '40vh', alignItems: 'center' }}>
          <CircularProgress sx={{ color: '#1398aa' }} />
        </Box>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Fade triggerOnce={fadeTriggerOnce}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '800', fontSize: '1.2rem', marginBottom: '2rem', color: '#ae39ed', textAlign: 'center' }}>
            {sectionLabel}
          </h1>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '700', fontSize: '48px', textAlign: 'center' }}>{title}</h1>
          <img src={hr} alt="" width="80" height="50" />
        </div>
        {body ? (
          <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
            <StrapiRichText source={body} sx={servicePageBodySx} />
          </Container>
        ) : (
          <StaticFallback />
        )}
        <Footer />
      </Fade>
    </div>
  );
}
